import { z } from 'zod';
// The ACCESS FACET's typed service entries — `assets.device_access` (jsonb).
// The asset's third facet (beside operational + books): how machines reach and
// talk to THIS unit. The CMDB is the official home collectors build their
// configs FROM; collection config (which OIDs, schedules, which collector) is
// the monitoring vertical's and never lives here. The facet's human word is
// CONNECTIVITY everywhere (tab · Edit-menu row · wizard step · drawer title —
// ADR-063); permission keys and wire names keep "access".
//
// Discipline (the integrations.config precedent, ADR-004):
//   • one Zod schema per service kind — a NEW KIND IS A CODE CHANGE, never a
//     migration;
//   • secret fields (the `secret` entries of DEVICE_ACCESS_FIELDS) are sealed
//     at rest (AES-256-GCM, ENCRYPTION_KEY) by the service layer and are
//     WRITE-ONLY on the wire: view shapes never return them (a `secrets_set`
//     marker list at most); changing one means re-entering it; the ONE human
//     door to a plaintext is the audited per-field reveal;
//   • the facet is writable only when the asset is network-capable
//     (asset_models.is_network_device ?? the category's connectivity state —
//     an `na` category rejects even the model override, ADR-067);
//   • the address half (IP · path · via · ports · usernames · markers · the
//     cellular + vpn reach facts) rides `asset:read`; `asset.private_fields:
//     read` gates the reveal alone; editing rides `asset:write` (the edit verb).
//
// ONE declaration drives every surface (the observation-registry lesson): the
// Zod schemas validate; DEVICE_ACCESS_FIELDS describes the same fields for
// rendering (the Connectivity tab's cards, the drawer's blocks) so a kind can
// never be askable in one place and invisible in another.
const port = z.number().int().min(1).max(65535);
const secret = z.string().min(1).max(255); // write-shape raw secret; sealed at rest
const short = z.string().trim().min(1).max(64);
// A SIM's own number — E.164 shape. A light structural check on purpose: the
// shared libphonenumber refine (common/phone) would pull the numbering-plan
// metadata into the assets chunk for a field the device never dials itself.
const msisdn = z.string().trim().regex(/^\+[1-9]\d{6,14}$/, 'PHONE_E164');
// The SIM's identity anchor (E.212 IMSI — declared because the device can
// never report its own NUMBER, so the number/APN facts ride on the IMSI;
// the declared↔observed IMSI pair is the SIM-/router-movement detector).
const imsi = z.string().trim().regex(/^\d{6,15}$/, 'IMSI_DIGITS');
// An IP address, v4 or v6 — validated wherever one is typed (`device_ip` is an
// inet column: unvalidated text would die as a DB cast error; the jsonb APN
// fields would store junk silently). v4 is exact; v6 covers the standard
// textual forms incl. `::` compression (zone ids / v4-embedded forms are out —
// the verb layer's cast-error mapping is the residual net). No hostnames by
// design (ADR-063: the fleet list is the evidence that would flip this).
const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const IPV6_RE = /^(([0-9a-f]{1,4}:){7}[0-9a-f]{1,4}|([0-9a-f]{1,4}:){1,7}:|([0-9a-f]{1,4}:){1,6}:[0-9a-f]{1,4}|([0-9a-f]{1,4}:){1,5}(:[0-9a-f]{1,4}){1,2}|([0-9a-f]{1,4}:){1,4}(:[0-9a-f]{1,4}){1,3}|([0-9a-f]{1,4}:){1,3}(:[0-9a-f]{1,4}){1,4}|([0-9a-f]{1,4}:){1,2}(:[0-9a-f]{1,4}){1,5}|[0-9a-f]{1,4}:((:[0-9a-f]{1,4}){1,6})|:((:[0-9a-f]{1,4}){1,7}|:))$/i;
export const isIpAddress = (v) => IPV4_RE.test(v) || IPV6_RE.test(v);
export const IpAddressSchema = z.string().trim().min(1).max(45).refine(isIpAddress, 'IP_ADDRESS_INVALID');
const address = IpAddressSchema;
export const DEVICE_ACCESS_KINDS = [
    'snmp_v1',
    'snmp_v2c',
    'snmp_v3',
    'modbus_tcp',
    'modbus_rtu',
    'web',
    'ssh',
    'ftp',
    'vpn',
    'cellular',
];
export const DeviceAccessKindSchema = z.enum(DEVICE_ACCESS_KINDS);
// `local_port` (the forward-remap case): a unit reached VIA a front dials
// `port` ON the front; when the forward re-maps to a different port inside
// (dial 8443 → inside 8080), the inside truth keeps its home here — display +
// on-site tests, NEVER the dial. Rendered on Via units only, defaulting to the
// kind's standard. Findings police the DIAL, never the local label (ADR-063).
// SNMP v1 and v2c share one shape: the dialed agent port (the device's own, or
// the port dialed on the front when reached Via) + the community strings.
// `community_trap` is what the device STAMPS in the traps it sends — recorded
// for provisioning/config truth; the trap RECEIVER does not check it (sender
// identity = source address; granting the receiver's key that one secret is a
// deliberate future lever — ADR-063). v1 is a third of the real fleet
// (the Trion controller family, the Fcu cooling controller, fuel sensors).
export const SnmpV1EntrySchema = z.object({
    kind: z.literal('snmp_v1'),
    port: port.optional(), // default 161
    local_port: port.optional(),
    community_read: secret.optional(),
    community_write: secret.optional(),
    community_trap: secret.optional(),
});
export const SnmpV2cEntrySchema = z.object({
    kind: z.literal('snmp_v2c'),
    port: port.optional(), // default 161
    local_port: port.optional(),
    community_read: secret.optional(),
    community_write: secret.optional(),
    community_trap: secret.optional(),
});
export const SNMP_AUTH_PROTOCOLS = ['sha', 'sha256', 'sha512', 'md5'];
export const SNMP_PRIV_PROTOCOLS = ['aes', 'aes256', 'des'];
export const SnmpV3EntrySchema = z.object({
    kind: z.literal('snmp_v3'),
    port: port.optional(),
    local_port: port.optional(),
    username: short,
    auth_protocol: z.enum(SNMP_AUTH_PROTOCOLS).optional(),
    auth_key: secret.optional(),
    priv_protocol: z.enum(SNMP_PRIV_PROTOCOLS).optional(),
    priv_key: secret.optional(),
});
export const ModbusTcpEntrySchema = z.object({
    kind: z.literal('modbus_tcp'),
    port: port.optional(), // default 502
    local_port: port.optional(),
    unit_id: z.number().int().min(0).max(255).optional(),
});
export const ModbusRtuEntrySchema = z.object({
    kind: z.literal('modbus_rtu'),
    unit_id: z.number().int().min(0).max(255).optional(),
    baud_rate: z.number().int().positive().optional(),
});
// The web door is SCHEME + ONE PORT — never a stored URL (ADR-063 rev): the
// composed remote door = scheme + the chain-root address + port. Where a
// product's HTTP API authenticates with the web login (`http-api` capability),
// this entry's credentials ARE the API access — no per-unit API fields exist.
export const WEB_SCHEMES = ['http', 'https'];
export const WEB_SCHEME_DEFAULT_PORT = { http: 80, https: 443 };
export const WebEntrySchema = z.object({
    kind: z.literal('web'),
    scheme: z.enum(WEB_SCHEMES).optional(), // display default http
    port: port.optional(), // default by scheme (80 / 443)
    local_port: port.optional(),
    username: short.optional(),
    password: secret.optional(),
});
export const SshEntrySchema = z.object({
    kind: z.literal('ssh'),
    port: port.optional(), // default 22
    local_port: port.optional(),
    username: short.optional(),
    password: secret.optional(),
});
export const FtpEntrySchema = z.object({
    kind: z.literal('ftp'),
    port: port.optional(), // default 21
    local_port: port.optional(),
    username: short.optional(),
    password: secret.optional(),
});
// The VPN reach kind (ADR-063 rev — the reach group's second member beside
// cellular: the facts behind my address). Offered when the model declares the
// `vpn` capability (supports ≠ uses: the product token says the box CAN, the
// unit's flavor says which this one runs). v1 field = the flavor ONLY;
// `device_ip` IS the reserved overlay address (no second "reserved" field);
// tunnel STATE (bootstrap/production · connected-since) is observed and
// arrives with monitoring; config files (.ovpn / WireGuard) land with the
// platform files surface behind the audited download.
export const VPN_FLAVORS = ['openvpn', 'wireguard'];
export const VpnEntrySchema = z.object({
    kind: z.literal('vpn'),
    flavor: z.enum(VPN_FLAVORS),
});
// The cellular REACH kind — a SLOT RECORD (ADR-063 rev 2026-08-30): per fitted
// slot, sheet-fed, all optional, ZERO secrets — IMSI (the identity anchor) ·
// the SMS control number · the operator's fixed APN allocation + its backup
// (the dial-direct fallback when the overlay is down; the compare accepts
// either). `slot` is required in DATA but never asked in the UI ("Add SIM"
// progressive rows, sequential auto-assignment; import is positional —
// `sim1_*`/`sim2_*` column groups, the group IS the slot). ICCID stays
// observed-only; eSIM is additive at the first real case (APP-315). SMS
// control authenticates device-side (sender whitelist + SMS password — a
// provisioning-checklist line, never a register column).
export const SimSlotSchema = z.object({
    slot: z.number().int().min(1).max(4),
    imsi: imsi.optional(),
    msisdn: msisdn.optional(),
    apn_ip: address.optional(),
    apn_ip_backup: address.optional(),
});
export const CellularEntrySchema = z.object({
    kind: z.literal('cellular'),
    sims: z
        .array(SimSlotSchema)
        .min(1)
        .max(4)
        .refine((rows) => new Set(rows.map((r) => r.slot)).size === rows.length, {
        message: 'one record per slot',
    }),
});
export const DeviceAccessEntrySchema = z.discriminatedUnion('kind', [
    SnmpV1EntrySchema,
    SnmpV2cEntrySchema,
    SnmpV3EntrySchema,
    ModbusTcpEntrySchema,
    ModbusRtuEntrySchema,
    WebEntrySchema,
    SshEntrySchema,
    FtpEntrySchema,
    VpnEntrySchema,
    CellularEntrySchema,
]);
// The facet: at most one entry per kind (v1 — a second web portal is a
// host/port override inside the entry, not a second entry; multiple SIMs ride
// INSIDE the one cellular entry as slot records).
export const DeviceAccessWriteSchema = z
    .array(DeviceAccessEntrySchema)
    .max(DEVICE_ACCESS_KINDS.length)
    .refine((entries) => new Set(entries.map((e) => e.kind)).size === entries.length, {
    message: 'one entry per service kind',
});
const LOCAL_PORT = { key: 'local_port', kind: 'port', viaOnly: true };
export const DEVICE_ACCESS_FIELDS = {
    snmp_v1: [
        { key: 'port', kind: 'port', defaultValue: 161 },
        LOCAL_PORT,
        { key: 'community_read', kind: 'secret' },
        { key: 'community_write', kind: 'secret' },
        { key: 'community_trap', kind: 'secret' },
    ],
    snmp_v2c: [
        { key: 'port', kind: 'port', defaultValue: 161 },
        LOCAL_PORT,
        { key: 'community_read', kind: 'secret' },
        { key: 'community_write', kind: 'secret' },
        { key: 'community_trap', kind: 'secret' },
    ],
    snmp_v3: [
        { key: 'port', kind: 'port', defaultValue: 161 },
        LOCAL_PORT,
        { key: 'username', kind: 'text', required: true },
        { key: 'auth_protocol', kind: 'enum', options: SNMP_AUTH_PROTOCOLS },
        { key: 'auth_key', kind: 'secret' },
        { key: 'priv_protocol', kind: 'enum', options: SNMP_PRIV_PROTOCOLS },
        { key: 'priv_key', kind: 'secret' },
    ],
    modbus_tcp: [
        { key: 'port', kind: 'port', defaultValue: 502 },
        LOCAL_PORT,
        { key: 'unit_id', kind: 'int', min: 0, max: 255 },
    ],
    modbus_rtu: [
        { key: 'unit_id', kind: 'int', min: 0, max: 255 },
        { key: 'baud_rate', kind: 'int', min: 1 },
    ],
    web: [
        // The port default derives from the SCHEME (80 / 443) — the one dynamic
        // default; surfaces read WEB_SCHEME_DEFAULT_PORT, never a static value here.
        { key: 'scheme', kind: 'enum', options: WEB_SCHEMES },
        { key: 'port', kind: 'port' },
        LOCAL_PORT,
        { key: 'username', kind: 'text' },
        { key: 'password', kind: 'secret' },
    ],
    ssh: [
        { key: 'port', kind: 'port', defaultValue: 22 },
        LOCAL_PORT,
        { key: 'username', kind: 'text' },
        { key: 'password', kind: 'secret' },
    ],
    ftp: [
        { key: 'port', kind: 'port', defaultValue: 21 },
        LOCAL_PORT,
        { key: 'username', kind: 'text' },
        { key: 'password', kind: 'secret' },
    ],
    vpn: [{ key: 'flavor', kind: 'enum', options: VPN_FLAVORS, required: true }],
    // The cellular entry renders as SIM slot rows, never a flat card — a DESIGNED
    // layout (the number headlines; the rest are labeled facts), not a generic
    // field loop, so the SIM fields need no render registry. Their one declaration
    // is the wire schema (SimSlot / SimRowSchema); the two renderers place each by
    // hand (a field-descriptor registry only earns its keep for the generic,
    // many-kinds service rendering above).
    cellular: [],
};
// Which fields per kind are secrets — sealed at rest, stripped from view
// shapes. DERIVED from the registry so the seal layer and the cards can never
// disagree about what is a credential. (The reach kinds — vpn · cellular —
// deliberately carry none.)
export const DEVICE_ACCESS_SECRET_FIELDS = Object.fromEntries(DEVICE_ACCESS_KINDS.map((k) => [k, DEVICE_ACCESS_FIELDS[k].filter((f) => f.kind === 'secret').map((f) => f.key)]));
// The one secret without which a declared service cannot be used — the
// `asset.credential_gap` family's law: a declared entry whose kind has a
// primary secret, with that secret unset. Kinds without one never gap.
export const DEVICE_ACCESS_PRIMARY_SECRET = {
    snmp_v1: 'community_read',
    snmp_v2c: 'community_read',
    snmp_v3: 'auth_key',
    web: 'password',
    ssh: 'password',
    ftp: 'password',
};
// How the kinds group — what a collector polls/controls, the human management
// doors, and the reach facts behind my address (the local-model capability
// ticks and any chooser read this, never a hand-ordered list).
export const DEVICE_ACCESS_KIND_GROUPS = {
    polling: ['snmp_v1', 'snmp_v2c', 'snmp_v3', 'modbus_tcp', 'modbus_rtu'],
    management: ['web', 'ssh', 'ftp'],
    reach: ['vpn', 'cellular'],
};
// The credential-bearing SERVICE kinds (polling + management) — what the
// device-targets read's per-credential narrowing list may name (DATA-CONTRACTS
// §device targets: an entry travels whole or not at all; the reach kinds carry
// zero secrets by design and always serve, so they are not narrowable).
export const DEVICE_ACCESS_SERVICE_KINDS = [
    ...DEVICE_ACCESS_KIND_GROUPS.polling,
    ...DEVICE_ACCESS_KIND_GROUPS.management,
];
export const DeviceAccessServiceKindSchema = z.enum(DEVICE_ACCESS_SERVICE_KINDS);
/** Normalize a kinds narrowing list: dedupe + registry order (the
 *  normalizeCapabilities discipline) — so storage, display, and the targets
 *  ETag's kinds digest compare cleanly regardless of caller list shape. */
export function normalizeDeviceTargetKinds(kinds) {
    const set = new Set(kinds);
    return DEVICE_ACCESS_SERVICE_KINDS.filter((k) => set.has(k));
}
// ── the PRODUCT's connectivity — what the box can do (the four-layer law) ────
// Which protocols a device talks is a datasheet fact on the MODEL (canonical
// rows transcribe the catalog's `capabilities`; local rows are ticked at
// birth; generic rows carry none) — the unit inherits the service list and
// carries only its own facts. The token list mirrors ems-catalog
// `schema/vocabularies.yaml capabilities`; extending it is a catalog admission
// (`vpn` = client support as a nameplate fact — gateways-by-evidence, never by
// category; the loader is token-tolerant, so admission ordering is free).
export const DEVICE_CAPABILITIES = [
    'snmp-v1',
    'snmp-v2c',
    'snmp-v3',
    'modbus-rtu',
    'modbus-tcp',
    'mqtt',
    'http-api',
    'web-ui',
    'ssh',
    'ftp',
    'vpn',
    'syslog',
    'log-files',
    'relay-io',
    'sms',
];
export const DeviceCapabilitySchema = z.enum(DEVICE_CAPABILITIES);
// Which capability opens which per-unit block. Tokens without a block are not
// access: `mqtt` is device-initiated (a future reach kind), `http-api` rides
// the WEB entry (same credentials — the display note says so; a distinct-
// credential API product births an `http_api` kind on evidence), `sms` rides
// the SIM rows (the SMS password authenticates device-side), `syslog` /
// `log-files` / `relay-io` are lanes and wiring, never doors.
export const CAPABILITY_ACCESS_KIND = {
    'snmp-v1': 'snmp_v1',
    'snmp-v2c': 'snmp_v2c',
    'snmp-v3': 'snmp_v3',
    'modbus-rtu': 'modbus_rtu',
    'modbus-tcp': 'modbus_tcp',
    'web-ui': 'web',
    ssh: 'ssh',
    ftp: 'ftp',
    vpn: 'vpn',
};
// How the CAPABILITY tokens group for the local-model ticks — the model-side
// mirror of DEVICE_ACCESS_KIND_GROUPS (several tokens open no per-unit block,
// so the tick UI cannot group by access kinds alone): what a collector polls,
// the human management doors, the reach facts, and the lanes/wiring marks.
export const DEVICE_CAPABILITY_GROUPS = {
    polling: ['snmp-v1', 'snmp-v2c', 'snmp-v3', 'modbus-rtu', 'modbus-tcp'],
    management: ['web-ui', 'ssh', 'ftp', 'http-api'],
    reach: ['vpn'],
    lanes: ['mqtt', 'syslog', 'log-files', 'sms', 'relay-io'],
};
/** Normalize a ticked capability set: dedupe + registry order — deterministic
 *  storage, so re-saves and the loader's change detection compare cleanly. */
export function normalizeCapabilities(tokens) {
    const set = new Set(tokens);
    return DEVICE_CAPABILITIES.filter((t) => set.has(t));
}
// ── the PRODUCT's reach hardware — FORMAT.md's five-key `interfaces` facet ───
// Nameplate counts, never roles or runtime config. Every key optional and
// written only when evidenced — a written count is ≥ 1, a written boolean is
// true, absent = none evidenced (the honest-record law). Mirrors ems-catalog
// FORMAT §The interfaces facet; new keys there are a format_version event.
// Deliberately NOT .strict(): a newer content bundle may carry keys this app
// release doesn't know — they're tolerated (and stripped at the wire).
export const ModelInterfacesSchema = z.object({
    /** Physical SIM slots. */
    sims: z.number().int().positive().optional(),
    /** An eSIM is offered (order-code variants fold in — the grain law). */
    esim: z.boolean().optional(),
    /** Total ethernet ports; `wan` only when the vendor's own nameplate roles one (LAN derives as ports − wan). */
    ethernet: z
        .object({ ports: z.number().int().positive(), wan: z.number().int().positive().optional() })
        .optional(),
    /** Serial ports by electrical standard. */
    serial: z
        .object({ rs232: z.number().int().positive().optional(), rs485: z.number().int().positive().optional() })
        .optional(),
    /** Units of this product can be dialed through — the Via population. A function fact, never derived from the gateway category. */
    fronts: z.boolean().optional(),
});
/** Write-side variant (local-model declarations): the authoring laws the
 *  catalog's validator enforces producer-side apply here app-side. */
export const ModelInterfacesWriteSchema = ModelInterfacesSchema.superRefine((v, ctx) => {
    if (v.ethernet?.wan !== undefined && v.ethernet.wan > v.ethernet.ports) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'ethernet.wan cannot exceed ethernet.ports', path: ['ethernet', 'wan'] });
    }
    if (v.sims !== undefined && v.sims > 4) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'sims is capped at 4', path: ['sims'] });
    }
});
/** Enforce the absence law on a written facet: zero counts, false booleans and
 *  empty sub-objects are DROPPED, never stored (absent = none evidenced). */
export function normalizeModelInterfaces(input) {
    const out = {};
    if (input.sims !== undefined && input.sims > 0)
        out.sims = input.sims;
    if (input.esim === true)
        out.esim = true;
    if (input.ethernet !== undefined && input.ethernet.ports > 0) {
        out.ethernet = { ports: input.ethernet.ports };
        if (input.ethernet.wan !== undefined && input.ethernet.wan > 0)
            out.ethernet.wan = input.ethernet.wan;
    }
    if (input.serial !== undefined) {
        const s = {};
        if (input.serial.rs232 !== undefined && input.serial.rs232 > 0)
            s.rs232 = input.serial.rs232;
        if (input.serial.rs485 !== undefined && input.serial.rs485 > 0)
            s.rs485 = input.serial.rs485;
        if (Object.keys(s).length > 0)
            out.serial = s;
    }
    if (input.fronts === true)
        out.fronts = true;
    return out;
}
/** The model's connectivity as the register reads it — null = no model OR a
 *  generic unit (no product to inherit from: address only until it is named). */
export const ModelConnectivitySchema = z
    .object({
    capabilities: z.array(DeviceCapabilitySchema),
    /** SIM slots the product has (0 = no modem) — the unit fills the populated ones. */
    sims: z.number().int().min(0).max(4),
    /** Can front other units (a gateway, a Modbus gateway, USB→Ethernet) — the Via picker's population. */
    fronts: z.boolean(),
    /** Local tier — the user curates these facts; canonical rows are the catalog's. */
    is_local: z.boolean(),
})
    .strict();
/** The per-unit service/reach kinds a product opens, in the registry's order.
 *  Cellular is the one kind NOT capability-opened — the product's SIM SLOTS
 *  (interfaces.sims) open the SIM rows instead. */
export function serviceKindsOfCapabilities(capabilities) {
    const kinds = new Set();
    for (const c of capabilities) {
        const k = CAPABILITY_ACCESS_KIND[c];
        if (k)
            kinds.add(k);
    }
    return DEVICE_ACCESS_KINDS.filter((k) => k !== 'cellular' && kinds.has(k));
}
// The derived access path (ADR-063's addressing law) — never a stored mode.
export const ACCESS_PATHS = ['direct', 'via', 'none'];
export const AccessPathSchema = z.enum(ACCESS_PATHS);
export function deriveAccessPath(input) {
    if (!input.network_capable)
        return 'none';
    if (input.accessed_via_asset_id)
        return 'via';
    return input.device_ip ? 'direct' : 'none';
}
